import React, { Component, Fragment } from 'react';
import axios from 'axios';
import { v4 } from 'uuid';
import moment from 'moment';
import logger from '@greenlight/logger';

import Message from '../../../components/UI/Message';
import Cleave from '../../../components/UI/CustomInputField/CustomCleaveField';
import Input from '../../../components/UI/CustomInputField';
import css from './AddChild.module.scss';
import inputCss from '../../../components/UI/CustomInputField/CustomInputField.module.scss';
import WidgetPropTypes from '../types';
import WidgetPagePropTypes from '../../WidgetPage/types';
import { REGISTRATION_URL } from '../../../actions/registration';
import Checkbox from '../../../components/UI/Checkbox/index.tsx';
import InclusivePronounSelection from '../../../components/InclusivePronounSelection/index.tsx';
import ConsentService from '../../../services/consent/index.ts';

export const WIDGET_NAME = 'add_child_info';
export const FIRST_NAME_ERROR_MESSAGE =
  'Please enter a first name at least 2 letters long';
export const LAST_NAME_ERROR_MESSAGE =
  'Please enter a last name at least 2 letters long';
export const PHONE_NUMBER_ERROR_MESSAGE = 'Please enter a valid phone number';
export const DOB_LENGTH_ERROR_MESSAGE =
  'Please enter a valid birthday in the format MM/DD/YYYY';
export const DOB_MISSING_YEAR_ERROR_MESSAGE = `Child's date of birth must have a year`;
export const DOB_AGE_ERROR_MESSAGE = 'Please enter a valid birthday';
export const DUPLICATE_PHONE_ERROR_MESSAGE =
  'Please make sure that each child has a unique phone number';
export const DUPLICATE_NAME_ERROR_MESSAGE =
  'Please make sure that each child has a unique name';
export const PRONOUN_ERROR_MESSAGE = 'Please select a pronoun';

const MAX_AGE = 110;
const VALID_NAME_LENGTH = 2;
const VALID_PHONE_NUMBER_LENGTH = 10;

export const ADD_CHILD_VALIDATION_ENDPOINT = `${REGISTRATION_URL}/query-with-params?actionType=check_valid_child`;

function formatDate(date) {
  if (date === null) return null;
  const parsedDate = moment(date, 'YYYY-MM-DD');
  return parsedDate.format('MM/DD/YYYY');
}

class AddChild extends Component {
  constructor(props) {
    super(props);

    this.state = {
      children: [
        {
          key: new Date().getTime(),
          error: null,
          errorMessage: null,
          dobErrorMessage: null,
          firstName: '',
          lastName: '',
          isNonAppUser: false,
          phoneNumber: '',
          dob: '',
          gender: '',
          childId: '',
          open: true,
          userHasExplicitlySetNonAppUser: false,
          label: ''
        }
      ]
    };
  }

  componentDidMount() {
    this.props.analytics.trackEvent('Registration Add Child Viewed');
    const { registeredUser, preregisteredUser } = this.props.user;

    this.createExistingChildren(registeredUser, preregisteredUser);
  }

  shouldEnableNextButton = child => {
    const phoneNumberFilled =
      (child &&
        child.phoneNumber &&
        child.phoneNumber.length > 4 &&
        !child.userHasExplicitlySetNonAppUser) ||
      child.phoneNumber.length === 0 ||
      child.phoneNumber.length === 10;
    const dobGenderFilled =
      child && child.gender && child.dob && child.dob.length > 3;
    return (
      child &&
      child.firstName &&
      child.lastName &&
      phoneNumberFilled &&
      dobGenderFilled
    );
  };

  cleanAddChildAnswer = child => {
    const childAnswerKeys = [
      'firstName',
      'lastName',
      'phoneNumber',
      'dob',
      'gender',
      'isNonAppUser',
      'childId'
    ];
    const addChildAnswer = childAnswerKeys.reduce(
      (answer, key) => (child[key] ? { ...answer, [key]: child[key] } : answer),
      {}
    );

    if (
      child.phoneNumber.length === 0 ||
      child.userHasExplicitlySetNonAppUser
    ) {
      addChildAnswer.isNonAppUser = true;
    } else {
      addChildAnswer.isNonAppUser = false;
    }
    return addChildAnswer;
  };

  componentDidUpdate(prevProps, prevState) {
    const { shouldEnable, widget, changeAnswerValue } = this.props;
    const { registeredUser, preregisteredUser } = this.props.user;

    if (
      preregisteredUser !== prevProps.user.preregisteredUser ||
      registeredUser !== prevProps.user.registeredUser
    ) {
      this.createExistingChildren(registeredUser, preregisteredUser);
    }

    if (prevState.children !== this.state.children) {
      this.under13Disclaimer(this.state.children);
      const child = this.state.children[this.state.children.length - 1];

      if (this.shouldEnableNextButton(child)) {
        const children = this.state.children.reduce((prev, curr) => {
          const childObj = this.cleanAddChildAnswer(curr);
          prev.push(childObj);
          return prev;
        }, []);
        shouldEnable(widget, true);
        changeAnswerValue(widget, children);
      } else {
        shouldEnable(widget, false);
      }
    }
  }

  createExistingChildren = (registeredUser, preregisteredUser) => {
    let existingChildren;
    if (preregisteredUser && preregisteredUser.children) {
      existingChildren = preregisteredUser.children;
    } else if (registeredUser && registeredUser.user) {
      existingChildren = registeredUser.user.children;
    }
    if (existingChildren && existingChildren.length) {
      const children = existingChildren.map(child => ({
        ...child,
        dob: formatDate(child.dob),
        phoneNumber: child.phoneNumber || child.phone || '',
        userHasExplicitlySetNonAppUser: !child.phoneNumber,
        childId: child.id.toString(),
        open: true
      }));
      this.setState({ children });
    }
  };

  under13Disclaimer = children => {
    const childrenUnder13 = children.some(
      child =>
        child.dob &&
        child.dob.length === 10 &&
        moment().diff(moment(child.dob, 'MM/DD/YYYY'), 'years') < 13
    );
  };

  errorChecking = async () => {
    const { children } = this.state;

    if (
      this.checkDuplicatePhoneNumbers(children) ||
      this.checkDuplicateNames(children)
    ) {
      throw new Error();
    }

    for (let i = 0; i < children.length; i += 1) {
      if (!this.validateKid(i)) {
        throw new Error();
      }
    }

    await Promise.all(
      children.map(async (child, i) => {
        if (!(await this.backEndValidateKid(i)) && child.error) {
          throw new Error();
        }
      })
    );

    this.props.analytics.trackEvent('Registration Pronoun Submitted', {
      eventProps: {
        pronouns: children.map(child => ({ gender: child.label }))
      }
    });

    try {
      const userId =
        window.analytics &&
        typeof window.analytics.user === 'function' &&
        (window.analytics.user().id() || window.analytics.user().anonymousId());
      await ConsentService.collectConsent({
        userId,
        type: 'coppa_disclaimer',
        page: 'Add Child Page'
      });
    } catch (e) {
      logger.error(new Error('Failed to collect COPPA consent', e));
    }
    return false;
  };

  invalidDob = dateOfBirth => {
    const age = moment().diff(moment(dateOfBirth, 'MM/DD/YYYY'), 'years');
    const minimumAge = 0;
    const childNotBorn =
      moment().diff(moment(dateOfBirth, 'MM/DD/YYYY'), 'days') < 0;
    return childNotBorn || age < minimumAge || age > MAX_AGE;
  };

  getValidator = () => {
    const validationChecksByMessage = new Map();

    validationChecksByMessage.set(
      FIRST_NAME_ERROR_MESSAGE,
      child => !this.isChildFirstNameValid(child)
    );
    validationChecksByMessage.set(
      LAST_NAME_ERROR_MESSAGE,
      child => !this.isChildLastNameValid(child)
    );
    validationChecksByMessage.set(
      PHONE_NUMBER_ERROR_MESSAGE,
      child =>
        !child.childId &&
        !(
          child.phoneNumber.length === 0 ||
          child.phoneNumber.length === VALID_PHONE_NUMBER_LENGTH
        )
    );
    validationChecksByMessage.set(
      PRONOUN_ERROR_MESSAGE,
      child => child.userHasExplicitlySetNonAppUser && !child.gender
    );
    validationChecksByMessage.set(
      DOB_AGE_ERROR_MESSAGE,
      child => !child.dob || this.invalidDob(child.dob)
    );
    validationChecksByMessage.set(
      DOB_MISSING_YEAR_ERROR_MESSAGE,
      child => child.dob && child.dob.length === 6
    );
    validationChecksByMessage.set(
      DOB_LENGTH_ERROR_MESSAGE,
      child => child.dob && child.dob.length < 10 && child.dob.length !== 6
    );
    return validationChecksByMessage.entries();
  };

  validateKid = index => {
    const child = this.state.children[index];
    const validator = this.getValidator();
    child.error = false;
    child.errorMessage = '';

    let validationCheck = validator.next();
    while (!validationCheck.done) {
      const [message, check] = validationCheck.value;
      if (check(child)) {
        child.error = true;
        child.errorMessage = message;
        break;
      }
      validationCheck = validator.next();
    }

    if (child.error) {
      window.scrollTo(0, 0);
      child.open = true;
      this.props.analytics.trackEvent('Registration Error Triggered', {
        eventProps: {
          registration_step: WIDGET_NAME,
          error_message: child.errorMessage
        }
      });
    }
    const allChildren = this.state.children;
    allChildren[index] = child;
    this.setState({ children: allChildren });
    return !child.error;
  };

  backEndValidateKid = async index => {
    const { children } = this.state;
    const child = children[index];
    this.props.startLoading({ isPlaid: false });

    try {
      await axios.post(
        ADD_CHILD_VALIDATION_ENDPOINT,
        {
          params: {
            first_name: child.firstName,
            last_name: child.lastName,
            phone_number: child.childId ? '' : child.phoneNumber,
            birth_date: moment(child.dob).format('YYYY-MM-DD')
          }
        },
        {
          headers: {
            Authorization: `Bearer ${this.props.app.registrationToken}`,
            'x-test-tracer': v4()
          }
        }
      );
      this.props.stopLoading();
      child.error = false;
      child.errorMessage = '';
      this.setState({ children });
      return true;
    } catch (err) {
      this.props.stopLoading();
      this.props.createError({
        message: 'There was an error adding one or more children',
        registrationStep: WIDGET_NAME
      });
      child.error = true;
      child.open = true;
      if (err.response.data) {
        child.errorMessage = err.response.data.message;
        this.props.analytics.trackEvent('Registration Error Triggered', {
          eventProps: {
            registration_step: WIDGET_NAME,
            error_message: child.errorMessage
          }
        });
        if (
          err.response.data.message === 'Unauthorized' ||
          err.response.data.message === 'Invalid Token' ||
          err.response.data.message === 'Invalid IP'
        ) {
          this.props.createError({
            message: err.response.data.message,
            registrationStep: WIDGET_NAME
          });
        }
      }
      this.setState({ children });
      throw err;
    }
  };

  checkDuplicatePhoneNumbers = children => {
    const phoneNumbers = children.reduce((numbers, child) => {
      if (child.phoneNumber) {
        numbers.push(child.phoneNumber);
      }
      return numbers;
    }, []);

    const isDuplicate = phoneNumbers.some(
      (child, i) => phoneNumbers.indexOf(child) !== i
    );

    if (isDuplicate) {
      this.props.createError({
        message: DUPLICATE_PHONE_ERROR_MESSAGE,
        registrationStep: WIDGET_NAME
      });
      return true;
    }
  };

  checkDuplicateNames = children => {
    const childrenNames = children.reduce((names, child) => {
      if (child.firstName && child.lastName) {
        names.push(`${child.firstName} ${child.lastName}`);
      }
      return names;
    }, []);

    const isDuplicate = childrenNames.some(
      (child, i) => childrenNames.indexOf(child) !== i
    );

    if (isDuplicate) {
      this.props.createError({
        message: DUPLICATE_NAME_ERROR_MESSAGE,
        registrationStep: WIDGET_NAME
      });
      return true;
    }
  };

  isChildFirstNameValid = child => child.firstName.length >= VALID_NAME_LENGTH;

  isChildLastNameValid = child => {
    if (child.lastName) {
      return child.lastName.length >= VALID_NAME_LENGTH;
    }
    return true;
  };

  onChange = which => value => {
    this.setState({ [which]: value });
  };

  onSubChildChanged = (index, which) => value => {
    const children = this.state.children.slice();
    if (which === 'phoneNumber') {
      children[index][which] = value.trim().replace(/^0+|\D/g, '');
    } else {
      children[index][which] = value;
    }
    this.setState({ children });
  };

  onSubChildToggled = (index, which) => () => {
    const children = this.state.children.slice();
    if (!children[index].childId) {
      children[index].phoneNumber = '';
      children[index][which] = !children[index][which];
    }
    this.setState({ children });
  };

  handleGenderChanged = (value, label, index) => {
    const children = this.state.children.slice();
    children[index].gender = value;
    children[index].label = label;
    this.setState({ children });
  };

  changeDateToFullYear = index => value => {
    if (value && value.length === 8) {
      const child = this.state.children[index];

      const shortDate = value;
      let fullDate = `${shortDate.substring(0, 6)}20${shortDate.substring(
        6,
        shortDate.length
      )}`;
      const age = moment().diff(moment(fullDate, 'MM/DD/YYYY'), 'years');
      if (age < 0) {
        fullDate = `${shortDate.substring(0, 6)}19${shortDate.substring(
          6,
          shortDate.length
        )}`;
      }

      child.dob = fullDate;
      const children = this.state.children.slice();
      children[index] = child;
      this.setState({ children });
    }
  };

  hasMessage = index => {
    const children = this.state.children.slice();
    return children[index].errorMessage;
  };

  addChild = e => {
    e.preventDefault();
    const existing = this.state.children.slice();

    if (!this.validateKid(this.state.children.length - 1)) {
      window.scrollTo(0, 0);
      this.onSubChildChanged(this.state.children.length - 1, 'error')(true);
      this.onSubChildChanged(
        this.state.children.length - 1,
        'errorMessage'
      )('Please complete the last child before adding another');
    } else {
      // Close all open children accordions before adding new child
      const newChild = {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        isNonAppUser: false,
        dob: '',
        gender: '',
        open: true,
        key: new Date().getTime(), // need a unique immutable key since we are mutating arrays for adding/removing children.
        userHasExplicitlySetNonAppUser: false
      };

      const children = existing
        .map(child => ({
          ...child,
          open: false
        }))
        .concat(newChild);

      this.setState({ children });
      window.scrollTo(
        0,
        document.querySelector(`#child-${this.state.children.length}`).offsetTop
      );
    }
  };

  deleteChild = (e, index) => {
    e.stopPropagation();
    const existing = this.state.children.slice();
    existing.splice(index, 1);
    this.setState({ children: existing });
  };

  toggleChildVisibility = index => {
    const child = this.state.children[index];

    child.open = !child.open;

    const allChildren = this.state.children;
    allChildren[index] = child;
    this.setState({ children: allChildren });
  };

  getInviteCopy = () => `We will invite them to download the Greenlight app`;

  getCheckboxLabel = () => (
    <span className={`${css.childNameText} fs-mask`}>
      Greenlight is a surprise and I don’t want to invite my child yet
    </span>
  );

  showPronounsLabel = child =>
    child.gender || !child.error ? (
      <p
        className={inputCss.formatLabel}
        style={{ top: '0', marginTop: '7px' }}
        data-e2e="gender-label"
      >
        So we know how to refer to your child when we send them communications
        about their account
      </p>
    ) : null;

  showDobLabel = child =>
    (child.dob.length === 10 && !this.invalidDob(child.dob)) || !child.error
      ? "To help us comply with regulations like the Children's Online Privacy Protection Act."
      : null;

  getChildOpenClassName = (child, openClassName, defaultClassName) =>
    child.open ? openClassName : defaultClassName;

  getPhoneNumberInputError = child => {
    let validPhone = false;

    if (
      child.phoneNumber.length > 0 &&
      child.phoneNumber.length < VALID_PHONE_NUMBER_LENGTH
    ) {
      validPhone = true;
    }

    return child.error && validPhone;
  };

  withError = (error, componentOrLogic) => error && componentOrLogic;

  getChildDobAndPronouns = (child, index) => (
    <div className={css.addChildDob}>
      <Cleave
        id={`child-dob-${index}`}
        placeholder={`${child.firstName || 'Child'}'s date of birth`}
        options={{
          date: true,
          datePattern: ['m', 'd', 'Y']
        }}
        value={child.dob || ''}
        onChange={this.onSubChildChanged(index, 'dob')}
        onBlur={this.changeDateToFullYear(index)}
        error={this.withError(
          child.error,
          !child.dob || this.invalidDob(child.dob) || child.dob.length < 10
        )}
        toggle={true}
        onClick={this.props.onClick}
        inputMode="numeric"
        format={this.showDobLabel(child)}
        secondaryFormat="By adding this child, you acknowledge you are their parent, legal guardian, or trustee."
        className="fs-mask"
        innerPlaceholder="MM/DD/YYYY"
        data-e2e="child-dob"
      />
      <div>
        <p className={css.genderSelectLabel}>
          How should we refer to your child?
        </p>
        <InclusivePronounSelection
          {...this.props}
          options={
            this.props.toggles.preferNotToSayOptionEnabled
              ? [
                  {
                    label: 'He/Him',
                    value: 'male',
                    'data-e2e': 'he_him_option',
                    'data-testid': 'he_him_option'
                  },
                  {
                    label: 'She/Her',
                    value: 'female',
                    'data-e2e': 'she_her_option',
                    'data-testid': 'she_her_option'
                  },
                  {
                    label: 'They/Them',
                    value: 'other',
                    'data-e2e': 'they_them_option',
                    'data-testid': 'they_them_option'
                  },
                  {
                    label: 'Prefer not to say',
                    value: 'other',
                    'data-e2e': 'prefer_not_to_say_option',
                    'data-testid': 'prefer_not_to_say_option'
                  }
                ]
              : [
                  {
                    label: 'he/him',
                    value: 'male',
                    'data-e2e': 'he_him_option',
                    'data-testid': 'he_him_option'
                  },
                  {
                    label: 'she/her',
                    value: 'female',
                    'data-e2e': 'she_her_option',
                    'data-testid': 'she_her_option'
                  },
                  {
                    label: 'they/them',
                    value: 'other',
                    'data-e2e': 'they_them_option',
                    'data-testid': 'they_them_option'
                  }
                ]
          }
          embedded
          selected={child.gender}
          onChange={(value, label) =>
            this.handleGenderChanged(value, label, index)
          }
          onClick={this.props.onClick}
          index={index}
          error={this.withError(child.error, !child.gender)}
        />
        {this.showPronounsLabel(child)}
      </div>
    </div>
  );

  render() {
    return (
      <div className={css.AddChildForm}>
        {this.state.children.map((child, index, array) => (
          <div
            key={child.key}
            id={`child-${index + 1}`}
            data-e2e={`child-${index + 1}`}
            data-testid={`child-${index + 1}`}
            className={css.childContainer}
          >
            <div
              className={`${this.getChildOpenClassName(
                child,
                css.childHeaderOpen,
                null
              )} ${css.childHeader}`}
              onClick={() => this.toggleChildVisibility(index)}
            >
              <div className={css.childHeaderLeft}>
                <p className={`${css.childHeaderName} fs-mask`}>{`${
                  child.firstName ||
                  `${
                    ['First', 'Second', 'Third', 'Fourth', 'Fifth'][index]
                  } Child` ||
                  'First Child'
                }`}</p>
                {array.length > 1 && !child.id && (
                  <a
                    className={css.removeChild}
                    onClick={e => this.deleteChild(e, index)}
                    data-e2e={`remove-child-${index + 1}`}
                    data-testid={`remove-child-${index + 1}`}
                  >
                    Remove child
                  </a>
                )}
              </div>
              <img
                className={this.getChildOpenClassName(child, css.openArrow, '')}
                src={'../../static/dropdown-arrow.svg'}
              />
            </div>
            <div
              className={css.childForm}
              style={{
                display: this.getChildOpenClassName(child, 'block', 'none')
              }}
            >
              {this.withError(
                child.error,
                <Message
                  error={child.error}
                  text={child.errorMessage || 'Please correct fields below'}
                />
              )}
              <div className={css.childName}>
                <Input
                  id={`child-first-name-${index}`}
                  placeholder={"Child's first name"}
                  autoComplete="off"
                  autoCorrect="off"
                  onClick={this.props.onClick}
                  type="text"
                  value={child.firstName}
                  error={this.withError(
                    child.error,
                    !this.isChildFirstNameValid(child)
                  )}
                  format="As it will appear on the card"
                  onChange={this.onSubChildChanged(index, 'firstName')}
                  className="fs-mask"
                  data-e2e="child-first-name"
                />
                <Input
                  id={`child-last-name-${index}`}
                  placeholder={`Child's last name`}
                  autoComplete="off"
                  onClick={this.props.onClick}
                  autoCorrect="off"
                  type="text"
                  value={child.lastName}
                  error={this.withError(
                    child.error,
                    !this.isChildLastNameValid(child)
                  )}
                  format="As it will appear on the card"
                  onChange={this.onSubChildChanged(index, 'lastName')}
                  className="fs-mask"
                  data-e2e="child-last-name"
                />
              </div>
              {!child.userHasExplicitlySetNonAppUser && (
                <Cleave
                  id={`child-mobile-number-${index}`}
                  placeholder={`${
                    child.firstName || 'Child'
                  }'s mobile number (optional)`}
                  options={{
                    delimiters: ['(', ')', ' ', '-'],
                    blocks: [0, 3, 0, 3, 4],
                    numericOnly: true,
                    phoneRegionCode: 'US'
                  }}
                  value={child.phoneNumber || ''}
                  error={this.getPhoneNumberInputError(child)}
                  onChange={this.onSubChildChanged(index, 'phoneNumber')}
                  format={!child.childId ? this.getInviteCopy() : ''}
                  disabled={!!child.childId}
                  inputMode="numeric"
                  onClick={this.props.onClick}
                  className="fs-mask"
                  data-e2e="child-mobile-number"
                />
              )}
              {!child.childId && (
                <>
                  <Checkbox
                    id={`checkbox_${index}`}
                    checked={child.userHasExplicitlySetNonAppUser}
                    onChange={this.onSubChildToggled(
                      index,
                      'userHasExplicitlySetNonAppUser'
                    )}
                    onClick={this.props.onClick}
                    data-e2e="no-phone-number"
                    label={this.getCheckboxLabel()}
                  />
                </>
              )}
              {this.getChildDobAndPronouns(child, index)}
            </div>
          </div>
        ))}
        {this.state.children.length < 5 && (
          <div
            onClick={e => this.addChild(e)}
            className={css.addChildButton}
            data-e2e="btn-add-child"
          >
            <img src={'../../static/add-child.svg'} alt="Add another child" />
            <p>Add another child</p>
          </div>
        )}
      </div>
    );
  }
}
AddChild.propTypes = {
  changeAnswerValue: WidgetPropTypes.changeAnswerValue.isRequired,
  index: WidgetPropTypes.index.isRequired,
  onClick: WidgetPropTypes.onClick.isRequired,
  shouldEnable: WidgetPropTypes.shouldEnable.isRequired,
  widget: WidgetPropTypes.widget.isRequired,
  app: WidgetPagePropTypes.app,
  createError: WidgetPagePropTypes.createError
};

export default AddChild;
